import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { openArcGISAuthPopup, exchangeCodeForToken } from '../utils/arcgisAuth';

const ArcGISAuthContext = createContext(null);

export function ArcGISAuthProvider({ children }) {
  const tokenRef = useRef(null);
  const [username, setUsername] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState(null);

  const signIn = useCallback(async () => {
    setSigningIn(true);
    setError(null);
    try {
      const { code } = await openArcGISAuthPopup();
      const { accessToken, username: user } = await exchangeCodeForToken(code);
      tokenRef.current = accessToken;
      setUsername(user || null);
      setIsSignedIn(true);
      return accessToken;
    } catch (err) {
      if (err.message !== 'Sign-in cancelled') {
        setError('ArcGIS sign-in failed: ' + err.message);
      }
      return null;
    } finally {
      setSigningIn(false);
    }
  }, []);

  const signOut = useCallback(() => {
    tokenRef.current = null;
    setUsername(null);
    setIsSignedIn(false);
    setError(null);
  }, []);

  const getToken = useCallback(() => tokenRef.current, []);

  // Call when a request fails due to expired/invalid token
  const invalidateToken = useCallback(() => {
    tokenRef.current = null;
    setIsSignedIn(false);
  }, []);

  const value = {
    getToken,
    invalidateToken,
    username,
    isSignedIn,
    signingIn,
    error,
    signIn,
    signOut,
  };

  return (
    <ArcGISAuthContext.Provider value={value}>
      {children}
    </ArcGISAuthContext.Provider>
  );
}

export function useArcGISAuth() {
  const ctx = useContext(ArcGISAuthContext);
  if (!ctx) throw new Error('useArcGISAuth must be used within ArcGISAuthProvider');
  return ctx;
}
